/**
 * Tracks in-flight MedChron exports per case/format and delegates the actual export call to
 * `MedChronExportService`. In-flight state lives in `medChronExportingStore` so every mounted
 * export control shares it — the customization dialog can close while the chronology header
 * keeps showing the export. `exportCase` resolves on success and rejects on failure; the
 * calling component decides what the user sees. `customizationOptions` is optional and passed
 * explicitly by the caller for a one-off export only — omitted, the server applies the user's
 * own saved preferences. It never reads the mutable global preferences store.
 */
import {
  ChronologySortField,
  ChronologyViewType,
  SortDirection,
} from "../components/MedicalChronology/Visits/types";
import type { MedChronExportCustomizationApiPayload } from "../services/medChronExportService";
import { exportingKey, useMedChronExportingStore } from "../store/medChronExportingStore";
import { useMedChronExportService } from "./useMedChronExportService";

export const ExportFormat = {
  PDF: "pdf",
  DOCX: "docx",
} as const;

export type ExportFormat = (typeof ExportFormat)[keyof typeof ExportFormat];

export type MedChronExportCustomizationOptions = {
  dataCustomization: {
    collapseTherapyVisits: boolean;
  };
  viewCustomization: {
    /** UI chronology view; DATE ("None") is omitted from the export API payload. */
    chronologyViewGroupBy: ChronologyViewType;
    chronologySortField: ChronologySortField;
    chronologySortDirection: SortDirection;
  };
};

export function useExportCase() {
  const exportService = useMedChronExportService();
  const exportingKeys = useMedChronExportingStore((state) => state.exportingKeys);
  const markExporting = useMedChronExportingStore((state) => state.markExporting);
  const clearExporting = useMedChronExportingStore((state) => state.clearExporting);

  const exportCase = (
    caseId: string,
    format: ExportFormat,
    caseName?: string | null,
    customizationOptions?: MedChronExportCustomizationApiPayload
  ): Promise<void> => {
    const key = exportingKey(caseId, format);
    markExporting(key);

    return exportService.export(caseId, format, caseName, customizationOptions).finally(() => {
      clearExporting(key);
    });
  };

  // Passing format checks that specific format; omitting checks any format for the case.
  const isExporting = (caseId: string, format?: ExportFormat) => {
    if (format) return exportingKeys.has(exportingKey(caseId, format));
    return Object.values(ExportFormat).some((fmt) => exportingKeys.has(exportingKey(caseId, fmt)));
  };

  return { exportCase, isExporting };
}
